import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../../EmptyState';
import { useVerification } from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { VerificationRequiredActions } from '@wix/da-shared-react/pkg/publicSession/types';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { PostsType } from '../../../../../../types/posts';
import { CTAModalsMap } from './constants';

import s from './EmptyState.scss';

export interface Props {
  postType: PostsType | 'featuredJournal' | 'featuredPosts';
  view?: 'large' | 'small' | 'wide';
  isOwner: boolean;
  username?: string;
  className?: string;
  openSubmitPostPopup?: () => void;
}

function getVerificationPopupType(
  postType: Props['postType']
): VerificationRequiredActions {
  switch (postType) {
    case 'journals':
    case 'drafts':
      return 'submitjournal';
    case 'polls':
      return 'submitpoll';
    case 'statuses':
      return 'submitstatus';
    default:
      return 'submit';
  }
}

const PostsEmptyState: React.FC<Props> = ({
  view,
  isOwner,
  username,
  postType,
  className,
  openSubmitPostPopup = noop,
}) => {
  const { t } = useTranslation();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const logBiEvent = useBiLogger();

  const modalType = CTAModalsMap[postType];

  const handleButtonClick = useCallback(() => {
    if (!modalType) {
      return null;
    }

    if (isVerificationNeeded) {
      pushVerificationPopup(getVerificationPopupType(postType));
    } else {
      openSubmitPostPopup();
      logBiEvent({
        evid: BiEventType.ADD_NEW_CONTENT_CLICK,
        widgetname: `${postType}_emptystate`,
      });
    }
  }, [
    isVerificationNeeded,
    logBiEvent,
    modalType,
    openSubmitPostPopup,
    postType,
    pushVerificationPopup,
  ]);

  return (
    <EmptyState
      view={view}
      className={classnames(
        s['root'],
        view === 'large' && s['large'],
        className
      )}
      title={
        isOwner
          ? t(`pages.posts.emptyState.${postType}.title.owner`)
          : t(`pages.posts.emptyState.${postType}.title.visitor`, {
              username,
            })
      }
      subtitle={
        isOwner
          ? t(`pages.posts.emptyState.${postType}.subtitle.owner`)
          : t(`pages.posts.emptyState.${postType}.subtitle.visitor`, {
              username,
            })
      }
      buttonLabel={
        modalType && t(`pages.posts.emptyState.${postType}.createCTA`)
      }
      onButtonClick={handleButtonClick}
      withButton={isOwner && modalType}
    />
  );
};
PostsEmptyState.displayName = 'PostsEmptyState';

export default PostsEmptyState;
